import { I18n } from "@assets/strings";
import React, { useState } from "react";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { CountryIndicator } from "@domain/currentProfile/countriesIndicators";
import { ClosableModal } from "@ui/modals/closableModal";
import { gap, mobile } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { TertiaryButton } from "@ui/components/buttons/tertiaryButton";
import { Description } from "@ui/components/containers/description";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { stack } from "@ui/components/layout";

interface UserKycPhonenumberCheckingModalProps extends React.HTMLAttributes<HTMLDivElement> {
	close: () => void;
	phoneNumber: string;
	indicative: CountryIndicator;
	onNumberChecked: (isChecked: boolean) => void;
}

const PREFIX = "T-";

export const UserKycPhonenumberCheckingModal: React.FC<UserKycPhonenumberCheckingModalProps> = ({
	close,
	phoneNumber,
	indicative,
	onNumberChecked,
	...htmlProps
}) => {
	const [code, setCode] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(false);
	const isCodeInvalid = code.length === 0;

	const { userKycService } = useServices();

	return (
		<Container title={I18n.t("kyc.verification.modal.title")} close={close} keyboardEventDisabled {...htmlProps}>
			<div />
			<Description>
				{I18n.t("kyc.verification.modal.description", { number: phoneNumber.slice(-2) })}
				<TertiaryButton
					onClick={async () => {
						//TODO add wording for toast
						try {
							await userKycService.checkNumber(indicative, phoneNumber);
							createToast(I18n.t("success"), I18n.t("kyc.verification.phone_number.toast.resend.success"), "success");
						} catch {
							createToast(I18n.t("error"), I18n.t("kyc.verification.phone_number.toast.resend.error"), "error");
						}
					}}
				>
					{I18n.t("kyc.verification.modal.resend_link")}
				</TertiaryButton>
			</Description>

			<InfoEdition>
				<PrimaryMedium>{I18n.t("kyc.verification.modal.edit.otp_code")}</PrimaryMedium>
				<InputWrapper>
					<Prefix>{PREFIX}</Prefix>
					<TextInput value={code} onChange={(e) => setCode(e.currentTarget.value)} />
				</InputWrapper>
			</InfoEdition>

			<LoadingButton
				onClick={async () => {
					//TODO add toast wording
					try {
						setLoading(true);
						const isVerified = await userKycService.checkCode(PREFIX.concat("", code));
						if (isVerified) {
							createToast(I18n.t("success"), I18n.t("kyc.verification.phone_number.toast.success.message"), "success");
							onNumberChecked(true);
							close();
						}
					} catch {
						onNumberChecked(false);
						createToast(I18n.t("error"), I18n.t("kyc.verification.phone_number.toast.error"), "error");
					} finally {
						setLoading(false);
					}
				}}
				loading={loading}
				disabled={isCodeInvalid}
			>
				{I18n.t("confirm")}
			</LoadingButton>
		</Container>
	);
};

const Container = styled(ClosableModal)`
	${gap(20)};

	> button {
		margin: 0 auto;
	}
	width: 500px !important;
	@media ${mobile} {
		width: 300px !important;
	}
`;

const InfoEdition = styled.div`
	${stack(10, "flex-start", "stretch")};
	width: 70%;
`;

const InputWrapper = styled.div`
	display: flex;
	align-items: center;
	max-width: 300px;
	padding: 10px 12px 9px;
	border-radius: 10px;
	border: 1px solid ${({ theme }) => theme.colors.textTertiary};
	padding-left: 0.5rem;
	overflow: hidden;

	@media (max-width: 370px) {
		width: 140px;
	}

	:focus-within {
		border: 1px solid ${({ theme }) => theme.colors.secondary};
	}
`;

const Prefix = styled.div`
	font-weight: 300;
	font-size: 14px;
	color: #999;
`;

const TextInput = styled.input<{ isInvalid?: boolean }>`
	flex-grow: 1;
	font-size: 0.935rem;
	outline: none;
	border: none;
`;
